import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CONSENT_EVENTS, getConsent, setConsent } from "../lib/consent";

const ease = [0.22, 1, 0.36, 1] as const;

export function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [detailed, setDetailed] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [userTriggered, setUserTriggered] = useState(false);
  const firstButtonRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    if (getConsent()) return;
    const t = window.setTimeout(() => setOpen(true), 1500);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    const onOpen = () => {
      const existing = getConsent();
      setAnalytics(existing?.analytics ?? false);
      setDetailed(true);
      setUserTriggered(true);
      setOpen(true);
    };
    window.addEventListener(CONSENT_EVENTS.open, onOpen);
    return () => window.removeEventListener(CONSENT_EVENTS.open, onOpen);
  }, []);

  useEffect(() => {
    if (open && userTriggered) {
      const t = window.setTimeout(() => firstButtonRef.current?.focus(), 60);
      return () => window.clearTimeout(t);
    }
  }, [open, userTriggered]);

  const decide = useCallback((value: boolean) => {
    setConsent({ analytics: value });
    setOpen(false);
    setDetailed(false);
    setUserTriggered(false);
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          aria-describedby={descId}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.35, ease }}
          className="fixed inset-x-0 bottom-0 z-[1000] px-3 pb-3 sm:px-6 sm:pb-6 pointer-events-none"
        >
          <div className="container-page max-w-3xl ml-auto mr-auto sm:mr-0 pointer-events-auto">
            <div className="rounded-2xl bg-white border border-brand-100 shadow-soft-lg overflow-hidden">
              <div className="p-4 sm:p-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="hidden sm:flex h-10 w-10 shrink-0 rounded-full bg-brand-50 text-brand-700 items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.75}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-5 w-5"
                      aria-hidden
                    >
                      <path d="M21 12a9 9 0 1 1-9-9 4 4 0 0 0 4 4 4 4 0 0 0 4 4 4 4 0 0 0 1 1Z" />
                      <path d="M8.5 10.5h.01M11.5 14.5h.01M15.5 11.5h.01M9.5 15.5h.01" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      id={titleId}
                      className="text-base sm:text-lg font-display font-semibold text-brand-900"
                    >
                      Votre vie privée
                    </p>
                    <p id={descId} className="mt-1 sm:mt-1.5 text-[13px] sm:text-sm text-ink-muted leading-relaxed">
                      Nous utilisons des cookies strictement nécessaires et, avec votre accord,
                      des cookies de mesure d'audience. Modifiable à tout moment depuis le pied
                      de page.{" "}
                      <Link
                        to="/politique-de-confidentialite"
                        className="text-brand-700 underline underline-offset-2 hover:text-brand-900"
                        onClick={() => setOpen(false)}
                      >
                        En savoir plus
                      </Link>
                      .
                    </p>
                  </div>
                </div>

                <AnimatePresence initial={false}>
                  {detailed && (
                    <motion.div
                      key="detail"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25, ease }}
                      className="overflow-hidden"
                    >
                      <div className="mt-5 space-y-3">
                        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-ink">
                                Strictement nécessaires
                              </div>
                              <p className="text-xs text-ink-muted mt-1">
                                Indispensables au bon fonctionnement du site (sécurité,
                                préférences de consentement). Toujours actifs.
                              </p>
                            </div>
                            <span className="text-xs font-semibold text-accent-700 bg-accent-500/15 px-2.5 py-1 rounded-full whitespace-nowrap">
                              Actifs
                            </span>
                          </div>
                        </div>

                        <label className="flex items-start justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4 cursor-pointer hover:border-brand-200 transition-colors">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-ink">
                              Mesure d'audience
                            </div>
                            <p className="text-xs text-ink-muted mt-1">
                              Statistiques anonymisées pour comprendre quelles pages vous
                              intéressent et améliorer le site.
                            </p>
                          </div>
                          <span className="relative inline-flex h-6 w-11 shrink-0 items-center">
                            <input
                              type="checkbox"
                              className="peer sr-only"
                              checked={analytics}
                              onChange={(e) => setAnalytics(e.target.checked)}
                            />
                            <span className="absolute inset-0 rounded-full bg-slate-300 transition-colors peer-checked:bg-accent-600 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-2" />
                            <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
                          </span>
                        </label>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3">
                  {detailed ? (
                    <>
                      <button
                        type="button"
                        onClick={() => decide(analytics)}
                        ref={firstButtonRef}
                        className="btn-primary btn-sm w-full sm:w-auto"
                      >
                        Enregistrer mes choix
                      </button>
                      <button
                        type="button"
                        onClick={() => decide(false)}
                        className="btn-ghost btn-sm w-full sm:w-auto"
                      >
                        Tout refuser
                      </button>
                      <button
                        type="button"
                        onClick={() => decide(true)}
                        className="btn-secondary btn-sm w-full sm:w-auto"
                      >
                        Tout accepter
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setAnalytics(getConsent()?.analytics ?? false);
                          setDetailed(true);
                        }}
                        className="btn-ghost btn-sm w-full sm:w-auto"
                      >
                        Personnaliser
                      </button>
                      <button
                        type="button"
                        onClick={() => decide(false)}
                        className="btn-ghost btn-sm w-full sm:w-auto"
                      >
                        Tout refuser
                      </button>
                      <button
                        type="button"
                        onClick={() => decide(true)}
                        className="btn-primary btn-sm w-full sm:w-auto"
                      >
                        Tout accepter
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
